import React, { useMemo, useState } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { LineChart } from 'react-native-gifted-charts';
import { useTheme } from '../../theme';
import {
  axisUnit,
  borderWidth,
  compactAxisTick,
  fs,
  s,
  v,
  formatMoney,
} from '../../utils/libs';
import ChartTooltip from './ChartTooltip';
import PositionedChartTooltip from './PositionedChartTooltip';
import { getLineChartPointX } from './chartTooltipPosition';

type Point = { week: string | number; ThisMonth: number; LastMonth: number };

type Props = {
  data: Point[];
  colorThis?: string;
  colorLast?: string;
};

const WeeklyComparisonChart: React.FC<Props> = ({
  data,
  colorThis,
  colorLast,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [containerW, setContainerW] = useState<number | null>(null);
  const yAxisLabelW = s(Platform.OS === 'web' ? 56 : 40);
  const chartWidth = useMemo(() => {
    if (!containerW) return 0;
    const measured = containerW - yAxisLabelW - s(16);
    return Math.max(measured, s(120));
  }, [containerW, yAxisLabelW]);
  const [rotatePad, setRotatePad] = useState(false);
  // Start chart at origin, avoid left offset
  const initialSpacing = s(10);
  const endSpacing = s(24);
  const pointerLabelW = s(150);
  const maxY = useMemo(() => {
    const values = (data || []).flatMap(p => [
      Number(p.ThisMonth || 0),
      Number(p.LastMonth || 0),
    ]);
    const max = Math.max(0, ...values);
    const rounded = Math.ceil(max / 10) * 10;
    return rounded === 0 ? 10 : rounded;
  }, [data]);
  const yUnit = useMemo(() => axisUnit(maxY), [maxY]);
  const thisLines = useMemo(
    () =>
      (data || []).map(p => ({
        value: Number(p.ThisMonth || 0),
        label: String(p.week),
        dataPointColor: colorThis || theme.colors.primary,
      })),
    [colorThis, data, theme.colors.primary],
  );
  const lastLines = useMemo(
    () =>
      (data || []).map(p => ({
        value: Number(p.LastMonth || 0),
        label: String(p.week),
        dataPointColor: colorLast || theme.colors.secondary,
      })),
    [colorLast, data, theme.colors.secondary],
  );
  const isEmpty =
    (thisLines?.length || 0) === 0 && (lastLines?.length || 0) === 0;

  const spacing = useMemo(() => {
    const points = Math.max(thisLines.length, lastLines.length, 1);
    const gaps = Math.max(points - 1, 1);
    const computed = (chartWidth - initialSpacing - endSpacing) / gaps;
    return Math.max(s(24), computed);
  }, [
    chartWidth,
    endSpacing,
    initialSpacing,
    thisLines.length,
    lastLines.length,
  ]);
  const shouldRotateX = useMemo(
    () =>
      thisLines.concat(lastLines).some(d => String(d.label || '').length >= 5),
    [thisLines, lastLines],
  );
  React.useEffect(() => {
    setRotatePad(shouldRotateX);
  }, [shouldRotateX]);

  const chartKey = useMemo(
    () => `${chartWidth}:${thisLines.length}:${lastLines.length}:${maxY}`,
    [chartWidth, thisLines.length, lastLines.length, maxY],
  );

  return (
    <View
      style={styles.container}
      onLayout={e => setContainerW(e.nativeEvent.layout.width)}>
      {containerW ? (
        <LineChart
          key={chartKey}
          data={thisLines}
          data2={lastLines}
          width={chartWidth}
          xAxisLength={chartWidth}
          rulesLength={chartWidth}
          endSpacing={endSpacing}
          spacing={spacing}
          disableScroll
          yAxisLabelWidth={yAxisLabelW}
          noOfSections={5}
          maxValue={maxY}
          color1={colorThis || theme.colors.primary}
          color2={colorLast || theme.colors.secondary}
          thickness={s(2)}
          yAxisTextStyle={{ color: theme.colors.text, fontSize: fs(11), }}
          xAxisLabelTextStyle={{
            color: theme.colors.text,
            fontSize: fs(11),
          }}
          height={v(Platform.OS === 'web' ? 300 : 200)}
          curved
          isAnimated
          labelsExtraHeight={0}
          xAxisTextNumberOfLines={1}
          showFractionalValues={false}
          formatYLabel={label =>
            compactAxisTick(Math.round(Number(label || '0')), yUnit)
          }
          pointerConfig={
            isEmpty
              ? undefined
              : {
                  pointer1Color: colorThis || theme.colors.info,
                  pointer2Color: colorLast || theme.colors.secondary,
                  pointerStripColor: theme.colors.border,
                  pointerStripWidth: borderWidth(
                    Platform.OS === 'web' ? 0.5 : 1,
                  ),
                  pointerLabelWidth: pointerLabelW,
                  pointerLabelHeight: v(80),
                  autoAdjustPointerLabelPosition: true,
                  activatePointersInstantlyOnTouch: true,
                  stripOverPointer: true,
                  persistPointer: false,
                  pointerVanishDelay: 5000,
                  pointerLabelComponent: (
                    _itemsForSet: any[],
                    _secondary: any[],
                    index: number,
                  ) => {
                    const maxLen = Math.max(thisLines.length, lastLines.length);
                    const safeIndex = Math.max(
                      0,
                      Math.min(index, Math.max(maxLen - 1, 0)),
                    );
                    const label =
                      thisLines[safeIndex]?.label ??
                      lastLines[safeIndex]?.label;
                    const v1 = Number(thisLines[safeIndex]?.value ?? 0);
                    const v2 = Number(lastLines[safeIndex]?.value ?? 0);
                    const pointX = getLineChartPointX({
                      index: safeIndex,
                      initialSpacing,
                      spacing,
                    });
                    return (
                      <PositionedChartTooltip
                        pointX={pointX}
                        tooltipWidth={pointerLabelW}
                        boundaryWidth={chartWidth}>
                        <ChartTooltip
                          header={String(label)}
                          items={[
                            {
                              color: colorThis || theme.colors.info,
                              label: 'This Month',
                              value: v1,
                              formatter: n => formatMoney(n),
                            },
                            {
                              color: colorLast || theme.colors.secondary,
                              label: 'Last Month',
                              value: v2,
                              formatter: n => formatMoney(n),
                            },
                          ]}
                        />
                      </PositionedChartTooltip>
                    );
                  },
                }
          }
        />
      ) : (
        <View
          style={{
            height: v(Platform.OS === 'web' ? 300 : 200),
          }}
        />
      )}
    </View>
  );
};

export default WeeklyComparisonChart;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      padding: s(8),
    },
  });
